/**
 * useRoofDrawing - Roof drawing and ruler tool state management
 *
 * Manages all drawing-related states including:
 * - Roof selection and addition modes
 * - Ruler measurement tool
 * - Drawing vertices
 * - Edit mode states
 */

import { useState, useCallback } from 'react';

export interface RoofDrawingState {
  // Mode flags
  isRoofSelectMode: boolean;
  isAddRoofMode: boolean;
  isDrawingRoof: boolean;
  isEditing: boolean;

  // Drawing vertices
  roofDrawingVertices: [number, number][];
  editedCoordinates: [number, number][] | null;

  // Ruler tool
  isRulerMode: boolean;
  rulerPoints: [number, number][];
  rulerCursorPos: [number, number] | null;
}

export interface RoofDrawingActions {
  // Mode setters
  setIsRoofSelectMode: (value: boolean) => void;
  setIsAddRoofMode: (value: boolean) => void;
  setIsDrawingRoof: (value: boolean) => void;
  setIsEditing: (value: boolean) => void;

  // Drawing setters
  setRoofDrawingVertices: React.Dispatch<React.SetStateAction<[number, number][]>>;
  setEditedCoordinates: (coords: [number, number][] | null) => void;

  // Ruler setters
  setIsRulerMode: (value: boolean) => void;
  setRulerPoints: React.Dispatch<React.SetStateAction<[number, number][]>>;
  setRulerCursorPos: (pos: [number, number] | null) => void;

  // Convenience methods
  startRoofDrawing: () => void;
  stopRoofDrawing: () => void;
  startRuler: () => void;
  stopRuler: () => void;
  addRulerPoint: (point: [number, number]) => void;
  clearRuler: () => void;
  clearAllModes: () => void;
}

export function useRoofDrawing(): RoofDrawingState & RoofDrawingActions {
  // Mode flags
  const [isRoofSelectMode, setIsRoofSelectMode] = useState(false);
  const [isAddRoofMode, setIsAddRoofMode] = useState(false);
  const [isDrawingRoof, setIsDrawingRoof] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Drawing vertices
  const [roofDrawingVertices, setRoofDrawingVertices] = useState<[number, number][]>([]);
  const [editedCoordinates, setEditedCoordinates] = useState<[number, number][] | null>(null);

  // Ruler tool
  const [isRulerMode, setIsRulerMode] = useState(false);
  const [rulerPoints, setRulerPoints] = useState<[number, number][]>([]);
  const [rulerCursorPos, setRulerCursorPos] = useState<[number, number] | null>(null);

  // Convenience methods
  const startRoofDrawing = useCallback(() => {
    setIsDrawingRoof(true);
    setIsAddRoofMode(true);
    setRoofDrawingVertices([]);
  }, []);

  const stopRoofDrawing = useCallback(() => {
    setIsDrawingRoof(false);
    setIsAddRoofMode(false);
    setRoofDrawingVertices([]);
  }, []);

  const startRuler = useCallback(() => {
    setIsRulerMode(true);
    setRulerPoints([]);
    setRulerCursorPos(null);
  }, []);

  const stopRuler = useCallback(() => {
    setIsRulerMode(false);
    setRulerPoints([]);
    setRulerCursorPos(null);
  }, []);

  const addRulerPoint = useCallback((point: [number, number]) => {
    setRulerPoints(prev => [...prev, point]);
  }, []);

  const clearRuler = useCallback(() => {
    setRulerPoints([]);
    setRulerCursorPos(null);
  }, []);

  const clearAllModes = useCallback(() => {
    setIsRoofSelectMode(false);
    setIsAddRoofMode(false);
    setIsDrawingRoof(false);
    setIsEditing(false);
    setIsRulerMode(false);
    setRoofDrawingVertices([]);
    setRulerPoints([]);
    setRulerCursorPos(null);
  }, []);

  return {
    // State
    isRoofSelectMode,
    isAddRoofMode,
    isDrawingRoof,
    isEditing,
    roofDrawingVertices,
    editedCoordinates,
    isRulerMode,
    rulerPoints,
    rulerCursorPos,

    // Setters
    setIsRoofSelectMode,
    setIsAddRoofMode,
    setIsDrawingRoof,
    setIsEditing,
    setRoofDrawingVertices,
    setEditedCoordinates,
    setIsRulerMode,
    setRulerPoints,
    setRulerCursorPos,

    // Convenience methods
    startRoofDrawing,
    stopRoofDrawing,
    startRuler,
    stopRuler,
    addRulerPoint,
    clearRuler,
    clearAllModes,
  };
}

export default useRoofDrawing;
